/**
 * 3D 분석 아래쪽 — 선택일 요약 · 지표 추이 · 일별 평균온도 (grosync-local 3D 분석 탭과 같은 구성).
 *
 * 지표는 날짜별 3D 결과 json 에 있는 값만 쓴다 (초장 · 총 엽면적 · 엽수(복엽) · 관폭).
 * 온도는 그 장비의 temp 센서 일별 평균 (/api/sensors/timeseries, KST 하루 버킷).
 */
import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { GrowthRecord } from '@grosync/common';
import { Panel } from '../../ui';
import { fetchTimeSeries } from '../../api/sensors';
import type { Growth3dResult } from './api';

export interface Trait {
  key: string;
  label: string;
  unit: string;
  definition: string;
  digits: number;
  pick: (r: Growth3dResult) => number | null | undefined;
}

export const TRAITS: Trait[] = [
  {
    key: 'height',
    label: '초장',
    unit: 'mm',
    definition: '3D 줄기 경로 — 관부 → 가장 긴 잎 끝',
    digits: 1,
    pick: (r) => r.height.height_mm,
  },
  {
    key: 'leaf_area',
    label: '총 엽면적',
    unit: 'cm²',
    definition: '3D 잎 판 넓이 합 (leaf_pipeline_v1)',
    digits: 1,
    pick: (r) => r.leaf_area?.leaf_area_cm2,
  },
  {
    key: 'leaf_count',
    label: '엽수',
    unit: '매',
    definition: '복엽 수',
    digits: 0,
    pick: (r) => r.leaf_area?.n_compound,
  },
  {
    key: 'canopy',
    label: '관폭',
    unit: 'mm',
    definition: '사진+깊이 측정 — 신뢰도가 낮으면 비움',
    digits: 1,
    pick: (r) => r.photo_measure?.canopy_width_mm,
  },
];

const KST_MS = 9 * 60 * 60 * 1000;
/** 버킷 시각(KST 자정) → KST 날짜 */
const kstDate = (iso: string): string =>
  new Date(Date.parse(iso) + KST_MS).toISOString().slice(0, 10);

export interface DayTemp {
  avg: number;
  min: number;
  max: number;
  count: number;
}

/** 장비의 temp 센서 일별 평균·최저·최고 (KST 날짜 → 값). 기록이 없으면 빈 Map */
export function useDailyTemp(deviceId: string, shots: GrowthRecord[]): Map<string, DayTemp> {
  const first = shots[0]?.recordedAt.slice(0, 10);
  const last = shots[shots.length - 1]?.recordedAt.slice(0, 10);
  const tempQ = useQuery({
    queryKey: ['growth3d-temp', deviceId, first, last],
    queryFn: () =>
      fetchTimeSeries({
        deviceId,
        sensorType: 'temp',
        from: new Date(`${first}T00:00:00+09:00`).toISOString(),
        to: new Date(`${last}T23:59:59+09:00`).toISOString(),
        interval: '1 day',
      }),
    enabled: !!deviceId && !!first && !!last,
    staleTime: 60_000,
    retry: false,
  });
  return useMemo(
    () =>
      new Map<string, DayTemp>(
        (tempQ.data ?? []).map((t) => [
          kstDate(t.timestamp),
          { avg: Number(t.avg), min: Number(t.min), max: Number(t.max), count: Number(t.count) },
        ]),
      ),
    [tempQ.data],
  );
}

const fmt = (v: number | null | undefined, digits: number): string =>
  v == null ? '—' : v.toFixed(digits);

export function Growth3dTrends({
  tempByDate,
  deviceName,
  shots,
  results,
  selected,
  trait,
  onTrait,
}: {
  tempByDate: Map<string, DayTemp>;
  deviceName: string;
  /** 오래된 날짜부터 */
  shots: GrowthRecord[];
  /** 기록 id → 그날 3D 결과 (없으면 null) */
  results: Map<string, Growth3dResult | null | undefined>;
  selected: string | null;
  trait: string;
  onTrait: (key: string) => void;
}): React.ReactElement {
  const rec = shots.find((r) => r.id === selected);
  const res = rec ? results.get(rec.id) : null;
  const meta = TRAITS.find((t) => t.key === trait) ?? TRAITS[0];
  const label = (r: GrowthRecord): string => r.recordedAt.slice(5, 10).replace('-', '/');

  const chart = shots.map((r) => {
    const v = results.get(r.id);
    return { label: label(r), value: v ? (meta.pick(v) ?? null) : null };
  });
  // 온도 그래프는 지표 그래프와 같은 날짜 축 — 날짜 집합이 어긋나면 위아래로 비교할 수 없다
  const tempChart = shots.map((r) => {
    const t = tempByDate.get(r.recordedAt.slice(0, 10));
    return { label: label(r), avg: t?.avg ?? null };
  });
  const hasTemp = tempChart.some((t) => t.avg != null);

  const today = rec ? tempByDate.get(rec.recordedAt.slice(0, 10)) : undefined;
  // 기간 평균 — 표본 수 가중
  let sum = 0;
  let n = 0;
  for (const r of shots) {
    const t = tempByDate.get(r.recordedAt.slice(0, 10));
    if (!t) continue;
    const w = t.count > 0 ? t.count : 1;
    sum += t.avg * w;
    n += w;
  }
  const periodAvg = n > 0 ? sum / n : null;

  const tooltipStyle: React.CSSProperties = {
    background: 'var(--md-sys-color-surface-container-high)',
    border: '1px solid var(--md-sys-color-outline-variant)',
    borderRadius: 11,
    font: '500 12px/16px var(--gs-font-kr)',
    color: 'var(--md-sys-color-on-surface)',
  };
  const tick = { fontSize: 11, fill: 'var(--md-sys-color-on-surface-variant)' };

  return (
    <>
      {/* 선택일 요약 */}
      <Panel
        title="선택일 요약"
        subtitle={
          rec
            ? `${dateLabel(rec.recordedAt)} · ${deviceName}${hasTemp ? '' : ' · 온도 기록 없음'}`
            : '—'
        }
        style={{ flexShrink: 0 }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(128px, 1fr))',
            gap: 12,
          }}
        >
          <EnvCell label="평균온도" value={fmt(today?.avg, 1)} />
          <EnvCell
            label="최저 / 최고"
            value={today ? `${today.min.toFixed(1)} / ${today.max.toFixed(1)}` : '—'}
          />
          <EnvCell label="기간 평균" value={fmt(periodAvg, 1)} />
          {TRAITS.map((t) => {
            const active = t.key === meta.key;
            return (
              <button
                key={t.key}
                onClick={() => onTrait(t.key)}
                title={t.definition}
                style={{
                  textAlign: 'left',
                  cursor: 'pointer',
                  padding: '11px 13px',
                  borderRadius: 13,
                  border: active
                    ? '2px solid var(--md-sys-color-primary)'
                    : '1px solid var(--md-sys-color-outline-variant)',
                  background: active
                    ? 'var(--md-sys-color-surface-container-low)'
                    : 'var(--md-sys-color-surface-container-lowest)',
                }}
              >
                <div
                  style={{
                    font: '500 11px/15px var(--gs-font-kr)',
                    color: 'var(--md-sys-color-on-surface-variant)',
                  }}
                >
                  {t.label}
                </div>
                <div
                  className="gs-num"
                  style={{
                    font: '700 22px/27px var(--gs-font-kr)',
                    letterSpacing: '-0.4px',
                    color: 'var(--md-sys-color-on-surface)',
                  }}
                >
                  {fmt(res ? t.pick(res) : null, t.digits)}
                  <span
                    style={{
                      font: '500 11px/14px var(--gs-font-kr)',
                      color: 'var(--md-sys-color-on-surface-variant)',
                      marginLeft: 3,
                    }}
                  >
                    {t.unit}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </Panel>

      {/* 추이 */}
      <Panel
        title={`${meta.label} 추이`}
        subtitle={`${shots.length}일 · ${deviceName}`}
        style={{ flexShrink: 0 }}
      >
        <div style={{ width: '100%', height: 270 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chart} margin={{ top: 16, right: 16, left: -10, bottom: 4 }}>
              <defs>
                <linearGradient id="g3dTrend" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--md-sys-color-primary)" stopOpacity={0.24} />
                  <stop offset="100%" stopColor="var(--md-sys-color-primary)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--md-sys-color-outline-variant)"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tick={tick}
                tickLine={false}
                axisLine={false}
                minTickGap={22}
              />
              <YAxis
                tick={tick}
                tickLine={false}
                axisLine={false}
                width={46}
                domain={['dataMin - 2', 'dataMax + 2']}
                tickFormatter={(v: number) => v.toFixed(0)}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v) => [`${String(v)} ${meta.unit}`, meta.label] as [string, string]}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="var(--md-sys-color-primary)"
                strokeWidth={2}
                fill="url(#g3dTrend)"
                connectNulls={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Panel>

      {/* 일별 평균온도 — 위 그래프와 같은 날짜 축 */}
      <Panel
        title="일별 평균온도"
        subtitle={hasTemp ? `${shots.length}일 · 같은 날짜 축` : '온도 기록 없음'}
        style={{ flexShrink: 0 }}
      >
        <div style={{ width: '100%', height: 190 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={tempChart} margin={{ top: 14, right: 16, left: -10, bottom: 4 }}>
              <defs>
                <linearGradient id="g3dTemp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--gs-warning, #e08a2e)" stopOpacity={0.26} />
                  <stop offset="100%" stopColor="var(--gs-warning, #e08a2e)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--md-sys-color-outline-variant)"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tick={tick}
                tickLine={false}
                axisLine={false}
                minTickGap={22}
              />
              <YAxis
                tick={tick}
                tickLine={false}
                axisLine={false}
                width={46}
                domain={['dataMin - 1', 'dataMax + 1']}
                tickFormatter={(v: number) => `${Math.round(v)}°`}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v) =>
                  [v == null ? '—' : `${Number(v).toFixed(1)} °C`, '평균'] as [string, string]
                }
              />
              <Area
                type="monotone"
                dataKey="avg"
                stroke="var(--gs-warning, #e08a2e)"
                strokeWidth={2}
                fill="url(#g3dTemp)"
                connectNulls={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Panel>
    </>
  );
}

/** 온도 한 칸. 지표 칸과 같은 모양이되 누를 수 없고 색만 다르다. */
function EnvCell({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div
      style={{
        padding: '11px 13px',
        borderRadius: 13,
        border: '1px solid var(--md-sys-color-outline-variant)',
        background: 'var(--md-sys-color-surface-container-lowest)',
      }}
    >
      <div
        style={{
          font: '500 11px/15px var(--gs-font-kr)',
          color: 'var(--md-sys-color-on-surface-variant)',
        }}
      >
        {label}
      </div>
      <div
        className="gs-num"
        style={{
          font: '700 22px/27px var(--gs-font-kr)',
          letterSpacing: '-0.4px',
          color: 'var(--gs-warning, var(--md-sys-color-tertiary))',
        }}
      >
        {value}
        <span
          style={{
            font: '500 11px/14px var(--gs-font-kr)',
            color: 'var(--md-sys-color-on-surface-variant)',
            marginLeft: 3,
          }}
        >
          °C
        </span>
      </div>
    </div>
  );
}

function dateLabel(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  const w = ['일', '월', '화', '수', '목', '금', '토'][d.getDay()];
  return `${d.getMonth() + 1}월 ${d.getDate()}일 (${w})`;
}
